import * as React from "react";
import { useState } from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { FileUpload } from "@/components/FileUpload";
import { FilePreviewModal } from "@/components/FilePreviewModal";
// Icons
import { ArrowLeft, CirclePlus, Save, Trash2 } from "lucide-react";

// Services
import { createIndicator } from "@/services/finance";
// import type { CreatePropertyRequest } from "@/types/finance";
// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";

const formatNumber = (value: string | number) => {
  if (!value && value !== 0) return "";

  // Convert to string and remove existing formatting
  let stringValue = String(value).replace(/[,\s]/g, "");

  // Handle parentheses
  const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
  if (isNegative) {
    stringValue = stringValue.slice(1, -1);
  }

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = stringValue.split(".");

  // Format integer part with commas
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Combine with decimal part if it exists
  const formattedValue =
    decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart.slice(0, 2)}`
      : formattedInteger;

  return isNegative ? `(${formattedValue})` : formattedValue;
};

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/indicators/add"
)({
  component: AddIndicators,
});

function AddIndicators() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const id = localStorage.getItem("companyId");
  const companyId = id?.replace(/^"(.+)"$/, "$1");
  const [year, setYear] = useState<number | undefined>(() => {
    const storedYear = localStorage.getItem("year");
    return storedYear ? Number(storedYear) : undefined;
  });
  const [yearError, setYearError] = React.useState<string>("");
  const yearInputRef = React.useRef<HTMLDivElement>(null);

  const [docFile, setDocFile] = React.useState("");

  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.INDICATOR_REPORT);
  const [revolvingAssets, setRevolvingAssets] = React.useState<
    Array<{
      name: string;
      amount: string;
      remark: string;
    }>
  >([
    { name: "ອາກອນລາຍໄດ້ທຸລະກິດທີ່ມອບໃຫ້ລັດແລ້ວ", amount: "", remark: "" },
    { name: "ອາກອນ ແລະ ພາສີຄ້າງຊໍາລະໃຫ້ລັດ", amount: "", remark: "" },
    { name: "ອາກອນ ແລະ ພາສີທີ່ຍົກເວັ້ນ", amount: "", remark: "" },
    {
      name: "ຄ່າທໍານຽມ ແລະ ຄ່າພາກຫຼວງທີ່ຈ່າຍໃຫ້ລັດແລ້ວ",
      amount: "",
      remark: "",
    },
    { name: "ເງິນປັນຜົນທີ່ຈ່າຍໃຫ້ລັດແລ້ວ", amount: "", remark: "" },
    { name: "ຍອດໜີ້ຕ້ອງສົ່ງຄ້າງຊໍາລະ", amount: "", remark: "" },
    {
      name: "ຍອດເງິນການຄໍ້າປະກັນທີ່ລັດຖະບານໃຫ້ການກູ້ຢືມ",
      amount: "",
      remark: "",
    },
    { name: "ການຊໍາລະເງິນກູ້ຄືນໃຫ້ລັດ", amount: "", remark: "" },
    { name: "ການຫັກເຂົ້າຄັງສໍາຮອງຕາມລະບຽບການ", amount: "", remark: "" },
    { name: "ການຫັກເຂົ້າຄັງອື່ນໆ", amount: "", remark: "" },
    { name: "ການປະກອບທຶນຂອງຝ່າຍລັດ", amount: "", remark: "" },
    {
      name: "ຜົນກໍາໄລຈາກໂຄງການລົງທຶນ ຫຼື ບໍລິສັດລູກ",
      amount: "",
      remark: "",
    },
    { name: "ລວມມູນຄ່າການລົງທຶນ ຫຼື ບໍລິສັດລູກ", amount: "", remark: "" },
    {
      name: "ລາຍຮັບກ່ອນຈ່າຍດອກເບ້ຍ ແລະ ອາກອນ (EBIT)",
      amount: "",
      remark: "",
    },
    { name: " ດອກເບ້ຍຕ້ອງຈ່າຍ", amount: "", remark: "" },
  ]);

  const [nonRevolvingAssets, setNonRevolvingAssets] = React.useState<
    Array<{
      name: string;
      amount: string;
      remark: string;
    }>
  >([
    {
      name: "ການມອບໝາຍໃຫ້ຜະລິດສິນຄ້າ ແລະ ການບໍລິການ",
      amount: "",
      remark: "",
    },
    {
      name: "ການເຂົ້າເຖິງຂອງສັງຄົມໃນການສະໜອງສິນຄ້າ ແລະ ການບໍລິການ",
      amount: "",
      remark: "",
    },
  ]);

  const mutation = useMutation({
    mutationFn: createIndicator,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["indicators", companyId, year],
      });

      toast({
        title: "ບັນທຶກສຳເລັດ",
        description: `ເພີ່ມຂໍ້ມູນ ປີ ${year} ສຳເລັດ`,
      });
      localStorage.setItem("year", JSON.stringify(year));

      navigate({ to: "/report/finance/company/indicators" });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      if (error.response?.data?.message === "INDICATOR_ALREADY_EXIST") {
        toast({
          title: "ບັນທຶກລົ້ມເຫຼວ",
          description: "ປີນີ້ມີຢູ່ໃນລະບົບແລ້ວ",
          variant: "destructive",
        });
      }

      if (error.response?.data?.message === "UN_PROCESSABLE_ENTITY") {
        toast({
          title: "ບັນທຶກລົ້ມເຫຼວ",
          description: "ກະລຸນາໃສ່ປີ",
          variant: "destructive",
        });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    console.log("Submitting form with companyId:", companyId);

    console.log("Year state value:", year);
    console.log("Year state yearError:", yearError);

    if (!companyId) return;
    if (!year) {
      setYearError("ກະລຸນາປ້ອນປີ");
      yearInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    const convertAmount = (amount: string) => {
      if (!amount) return 0;
      const cleanValue = amount.replace(/[,()]/g, "");
      const numValue = Number(cleanValue);
      return amount.includes("(") ? -numValue : numValue;
    };

    const indicatorDetails = [
      ...revolvingAssets.map((asset) => ({
        ...asset,
        amount: convertAmount(asset.amount),
        indicatorType: "INDICATOR" as const,
      })),
      ...nonRevolvingAssets.map((asset) => ({
        ...asset,
        amount: convertAmount(asset.amount),
        indicatorType: "PUBLIC_SECTOR" as const,
      })),
    ];

    const data = {
      companyId,
      yearly: year, // Use the year state instead of yearly
      docFile,
      indicatorDetails,
    };

    mutation.mutate(data as unknown as { [key: string]: string | number });
  };

  const addRevolvingAsset = () => {
    setRevolvingAssets([
      ...revolvingAssets,
      { name: "", amount: "", remark: "" },
    ]);
  };

  const addNonRevolvingAsset = () => {
    setNonRevolvingAssets([
      ...nonRevolvingAssets,
      { name: "", amount: "", remark: "" },
    ]);
  };

  const removeRevolvingAsset = (index: number) => {
    setRevolvingAssets(revolvingAssets.filter((_, i) => i !== index));
  };

  const removeNonRevolvingAsset = (index: number) => {
    setNonRevolvingAssets(nonRevolvingAssets.filter((_, i) => i !== index));
  };

  // Add total calculation functions
  // Update the calculation functions

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: { page: 1, startYear: 0, endYear: 0 },
              })
            }
            className="flex items-center gap-[4px] mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between">
            <div>
              <CardTitle>ເພີ່ມຂໍ້ມູນບັນດາຕົວຊີ້ວັດທີ່ຈະນຳໃຊ້ຂໍ້ມູນ</CardTitle>
              <CardDescription>ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບຖ້ວນ</CardDescription>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Year Selector */}

            <div className="flex flex-col gap-2 mb-4" ref={yearInputRef}>
              <Label htmlFor="yearly">
                ປີ <span className="text-red-500">*</span>
              </Label>
              <Select
                value={year ? String(year) : ""}
                onValueChange={(value) => {
                  setYear(Number(value));
                  setYearError(""); // Clear error when year is selected
                }}
                required
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="ກະລຸນາເລືອກປີ" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(
                    {
                      length: new Date().getFullYear() - 1974,
                    },
                    (_, i) => (
                      <SelectItem
                        key={i}
                        value={(new Date().getFullYear() - i).toString()}
                      >
                        {new Date().getFullYear() - i}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="[&>*]:border [&>*]:font-bold [&>*]:text-black">
                  <TableHead>ລາຍການ</TableHead>
                  <TableHead>ຈຳນວນ</TableHead>
                  <TableHead>ໝາຍເຫດ</TableHead>
                  <TableHead className="w-[100px]">ຈັດການ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Revolving Assets */}
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>
                    ບັນດາຕົວຊີ້ວັດທີ່ຈະໄດ້ນຳໃຊ້ຂໍ້ມູນ
                  </TableCell>
                </TableRow>
                {revolvingAssets.map((asset, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell className="flex gap-x-1 items-center">
                      {index + 1}.
                      <Input
                        value={asset.name}
                        onChange={(e) => {
                          const newAssets = [...revolvingAssets];
                          newAssets[index].name = e.target.value;
                          setRevolvingAssets(newAssets);
                        }}
                        readOnly={index < 15}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="text"
                        value={formatNumber(asset.amount)}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newAssets = [...revolvingAssets]; // or nonRevolvingAssets

                          // Allow empty input
                          if (!value) {
                            newAssets[index].amount = "";
                            setRevolvingAssets(newAssets);
                            return;
                          }

                          // Remove existing formatting
                          const rawValue = value.replace(/,/g, "");

                          // Check if the input matches our pattern (including decimals and parentheses)
                          if (/^[(]?\d*\.?\d*[)]?$/.test(rawValue)) {
                            newAssets[index].amount = rawValue;
                            setRevolvingAssets(newAssets);
                          }
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={asset.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) => {
                          const newAssets = [...revolvingAssets];
                          newAssets[index].remark = e.target.value;
                          setRevolvingAssets(newAssets);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {index > 14 ? (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() => removeRevolvingAsset(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                <Button
                  type="button"
                  variant={"ghost"}
                  size="icon"
                  onClick={addRevolvingAsset}
                  className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
                >
                  <CirclePlus className="w-4 h-4" />
                  ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                </Button>

                {/* Non-Revolving Assets */}
                <TableRow className="bg-[#216C4C]/20 [&>*]:font-bold [&>*]:text-black">
                  <TableCell colSpan={4}>ສະເພາະຂະແໜງສາທາລະນະປະໂຫຍດ</TableCell>
                </TableRow>
                {nonRevolvingAssets.map((asset, index) => (
                  <TableRow key={index} className="[&>*]:border">
                    <TableCell className="flex gap-x-1 items-center">
                      {index + 1}.
                      <Input
                        value={asset.name}
                        onChange={(e) => {
                          const newAssets = [...nonRevolvingAssets];
                          newAssets[index].name = e.target.value;
                          setNonRevolvingAssets(newAssets);
                        }}
                        readOnly={index < 2}
                      />
                    </TableCell>
                    <TableCell>
                      {/* <Input
												type="text"
												value={formatNumber(asset.amount)}
												onChange={(e) => {
													const rawValue = e.target.value.replace(
														/[^\d.-]/g,
														"",
													); // Remove all non-numeric chars except decimal and minus
													if (
														rawValue === "" ||
														/^-?\d*\.?\d*$/.test(rawValue)
													) {
														const newAssets = [...nonRevolvingAssets];
														newAssets[index].amount =
															parseFormattedNumber(rawValue);
														setNonRevolvingAssets(newAssets);
													}
												}}
												className="text-right"
											/> */}
                      <Input
                        type="text"
                        value={formatNumber(asset.amount)}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newAssets = [...nonRevolvingAssets]; // or nonRevolvingAssets

                          // Allow empty input
                          if (!value) {
                            newAssets[index].amount = "";
                            setNonRevolvingAssets(newAssets);
                            return;
                          }

                          // Remove existing formatting
                          const rawValue = value.replace(/,/g, "");

                          // Check if the input matches our pattern (including decimals and parentheses)
                          if (/^[(]?\d*\.?\d*[)]?$/.test(rawValue)) {
                            newAssets[index].amount = rawValue;
                            setNonRevolvingAssets(newAssets);
                          }
                        }}
                        className="text-right"
                        inputMode="numeric"
                        placeholder="1,000,000"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={asset.remark}
                        placeholder="ໝາຍເຫດ"
                        onChange={(e) => {
                          const newAssets = [...nonRevolvingAssets];
                          newAssets[index].remark = e.target.value;
                          setNonRevolvingAssets(newAssets);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {index > 1 ? (
                        <Button
                          type="button"
                          variant={"ghost"}
                          size="icon"
                          onClick={() => removeNonRevolvingAsset(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
                <Button
                  type="button"
                  variant={"ghost"}
                  size="icon"
                  onClick={addNonRevolvingAsset}
                  className="gap-2 p-4 my-2 w-auto font-bold text-blue-500 border-2 border-blue-500"
                >
                  <CirclePlus className="w-4 h-4" />
                  ເພີ່ມຊ່ອງປ້ອນຂໍ້ມູນ
                </Button>
              </TableBody>
            </Table>
            <div className="flex gap-x-1 items-center mt-4">
              {/* <Input
								type="file"
								value={docFile}
								onChange={(e) => setDocFile(e.target.value)}
								placeholder="ລິ້ງເອກະສານ"
								className="max-w-[300px]"
							/> */}

              <FileUpload
                currentFile={docFile}
                onFileUpload={(file) => {
                  console.log(file, "File Uploaded");
                  setDocFile(file);
                }}
              />
            </div>
            <div className="flex gap-x-2 justify-end mt-4">
              <Button
                type="button"
                onClick={() =>
                  navigate({
                    to: "/report/finance",
                    search: { page: 1, startYear: 0, endYear: 0 },
                  })
                }
                variant={"outline"}
              >
                ຍົກເລີກ
              </Button>
              <Button
                disabled={mutation.isPending}
                type="submit"
                className="flex gap-x-2 items-center"
              >
                {mutation.isPending ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    ບັນທຶກ
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
